'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineClear } from '@/app/components/icons/BaseLineClear';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineSearch } from '@/app/components/icons/BaseLineSearch';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Input } from '@/app/components/ui/input';
import { cn } from '@/app/utils';
import { FilterButton } from './FilterButton';
import type { FilterMenuItem } from './types';

interface MoreFiltersDropdownProps {
  items: FilterMenuItem[];
}

export const MoreFiltersDropdown = ({ items }: MoreFiltersDropdownProps) => {
  const t = useTranslations();
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});

  const setSearchQuery = (itemId: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [itemId]: value }));
  };

  const renderMenuItem = (item: FilterMenuItem) => {
    switch (item.type) {
      case 'dropdown': {
        if (!item.options || !item.onSelectionChange) return null;

        const query = searchQueries[item.id] || '';
        const normalizedQuery = query.trim().toLowerCase();
        const filteredOptions =
          item.searchable && normalizedQuery
            ? (item.options || []).filter((option) =>
                (option.searchLabel ?? option.label).toLowerCase().includes(normalizedQuery),
              )
            : item.options || [];

        return (
          <DropdownMenuSub key={item.id}>
            <DropdownMenuSubTrigger
              className="flex items-center justify-between rounded-md py-1.5 pl-2 font-normal text-sm text-white hover:bg-system-white24 hover:text-white focus:bg-system-white24 focus:text-white"
              disabled={item.disabled}
            >
              <span className="flex-1 text-left">{item.label}</span>
              <span className="flex items-center">
                {item.selectedValues && item.selectedValues.length > 0 && (
                  <>
                    {(() => {
                      const firstSelectedOption = item.options?.find((opt) => opt.key === item.selectedValues?.[0]);
                      const firstLabel = firstSelectedOption?.label || item.selectedValues?.[0];
                      return <span>{firstLabel?.slice(0, 6)}</span>;
                    })()}
                    <span>
                      {(item.selectedValues.length > 1 ||
                        (() => {
                          const firstSelectedOption = item.options?.find((opt) => opt.key === item.selectedValues?.[0]);
                          const firstLabel = firstSelectedOption?.label || item.selectedValues?.[0];
                          return firstLabel?.length > 6;
                        })()) &&
                        '...'}
                    </span>
                  </>
                )}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                className="max-h-[calc(100vh-300px)] space-y-1 overflow-y-auto border border-system-white12 bg-[#232529]"
                sideOffset={16}
              >
                {item.searchable && (
                  <div className="relative pb-1">
                    <Input
                      value={query}
                      onChange={(e) => setSearchQuery(item.id, e.target.value)}
                      placeholder={item.searchPlaceholder || t('creations.search.placeholder')}
                      className={cn(
                        'peer h-8 w-full min-w-0 rounded-8 border-none bg-system-white10 ps-8 text-sm placeholder:text-system-white36 focus-visible:bg-[#232933] focus-visible:text-system-white',
                        query ? 'pe-8' : 'pe-4',
                        'focus:border-system-white10',
                      )}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <BaseLineSearch className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-4 text-base text-system-white opacity-30 peer-focus-visible:text-system-white" />
                    {query && (
                      <BaseLineClear
                        className="-translate-y-1/2 absolute top-1/2 right-4 cursor-pointer text-base text-system-white opacity-80 transition-opacity hover:opacity-90"
                        onClick={() => setSearchQuery(item.id, '')}
                      />
                    )}
                  </div>
                )}
                <DropdownMenuCheckboxItem
                  key="All"
                  checked={item.selectedValues?.length === 0}
                  onSelect={(e) => e.preventDefault()}
                  onClick={() => {
                    if (!item.selectedValues || !item.onSelectionChange) return;
                    item.onSelectionChange([]);
                  }}
                >
                  {t('creations.filters.all')}
                </DropdownMenuCheckboxItem>
                {filteredOptions.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.key}
                    checked={item.selectedValues?.includes(option.key) || false}
                    onSelect={(e) => e.preventDefault()}
                    onClick={() => {
                      if (!item.selectedValues || !item.onSelectionChange) return;

                      const newSelection = item.selectedValues.includes(option.key)
                        ? item.selectedValues.filter((v) => v !== option.key)
                        : [...item.selectedValues, option.key];
                      item.onSelectionChange(newSelection);
                    }}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        );
      }

      case 'checkbox':
        return (
          <DropdownMenuItem
            key={item.id}
            className="flex items-center justify-start gap-2 rounded-md px-2 py-1.5 font-normal text-sm text-white hover:bg-system-white24 hover:text-white focus:bg-system-white24 focus:text-white"
            onClick={() => item.onCheckedChange?.(!item.checked)}
            disabled={item.disabled}
          >
            <CheckboxFill checked={item.checked || false} className="text-base" />
            <span>{item.label}</span>
          </DropdownMenuItem>
        );

      case 'button': {
        const IconComponent = item.icon;
        return (
          <DropdownMenuItem
            key={item.id}
            className="flex items-center justify-start gap-2 rounded-md px-2 py-1.5 font-normal text-sm text-white hover:bg-system-white24 hover:text-white focus:bg-system-white24 focus:text-white"
            onClick={item.onClick}
            disabled={item.disabled}
          >
            {IconComponent && <IconComponent className="text-base" />}
            <span>{item.label}</span>
          </DropdownMenuItem>
        );
      }

      default:
        return null;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <FilterButton>
          <BaseLineMore className="text-xl" />
        </FilterButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="max-h-[calc(100vh-300px)] w-[204px] min-w-fit max-w-[204px] space-y-1 overflow-y-auto border border-system-white12 bg-[#232529] p-2"
        sideOffset={8}
        align="start"
      >
        {items.map(renderMenuItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
